import { create } from "zustand";
import { API_ENDPOINTS, apiFetchJson } from "@/lib/api";

export type AlertLevel = "info" | "warning" | "critical";
export type AlertType = "system" | "security" | "gaming" | "user";

export interface SystemMetrics {
    cpu_percent: number;
    memory_percent: number;
    disk_percent: number;
    load_average: number[];
    network_io: { bytes_sent: number; bytes_recv: number };
    processes_count: number;
    uptime_seconds: number;
    timestamp: string;
}

export interface Alert {
    id: string;
    level: AlertLevel;
    type: AlertType;
    title: string;
    message: string;
    timestamp: string;
    resolved: boolean;
    metadata?: Record<string, any>;
}

export interface AlertSummary {
    critical: number;
    warning: number;
    info: number;
}

export interface ServiceHealth {
    service: string;
    status: "healthy" | "degraded" | "down";
    response_time_ms: number;
    last_check: string;
    details?: string;
}

interface MonitoringStoreState {
    systemMetrics: SystemMetrics | null;
    alerts: Alert[];
    alertsSummary: AlertSummary;
    serviceHealth: ServiceHealth[];
    isLoading: boolean;
    error: string | null;
}

interface MonitoringStoreActions {
    fetchSystemMetrics: () => Promise<void>;
    fetchAlerts: () => Promise<void>;
    fetchServiceHealth: () => Promise<void>;
    resolveAlert: (
        alertId: string,
    ) => Promise<{ success: true } | { success: false; error: string }>;
    refresh: () => Promise<void>;
    startPolling: () => void;
    stopPolling: () => void;
}

const _nowIso = () => new Date().toISOString();

let _pollRefCount = 0;
let _pollHandle: number | null = null;

export const useMonitoringStore = create<
    MonitoringStoreState & MonitoringStoreActions
>((set, get) => ({
    systemMetrics: null,
    alerts: [],
    alertsSummary: { critical: 0, warning: 0, info: 0 },
    serviceHealth: [],
    isLoading: false,
    error: null,

    fetchSystemMetrics: async () => {
        try {
            const [perf, sysInfo] = await Promise.all([
                apiFetchJson<any>(API_ENDPOINTS.SERVERS_PERFORMANCE),
                apiFetchJson<any>(API_ENDPOINTS.SYSTEM_INFO),
            ]);

            const system = perf?.system || {};
            const net = system?.network_io || {};

            const uptimeRaw: string = String(sysInfo?.uptime || "0s");
            const uptimeSeconds = Number.parseInt(
                uptimeRaw.replace(/[^\d]/g, "") || "0",
                10,
            );

            set({
                systemMetrics: {
                    cpu_percent: Number(system.cpu_usage ?? 0),
                    memory_percent: Number(system.memory_usage ?? 0),
                    disk_percent: Number(system.disk_usage ?? 0),
                    load_average: Array.isArray(system.load_average)
                        ? system.load_average
                        : [0, 0, 0],
                    network_io: {
                        bytes_sent: Number(net.bytes_sent ?? 0),
                        bytes_recv: Number(net.bytes_recv ?? 0),
                    },
                    processes_count: Number(sysInfo?.processes_total ?? 0),
                    uptime_seconds: Number.isFinite(uptimeSeconds) ? uptimeSeconds : 0,
                    timestamp: String(perf?.timestamp || _nowIso()),
                },
            });
        } catch (err) {
            console.error("Error fetching system metrics:", err);
            set({ systemMetrics: null });
        }
    },

    fetchAlerts: async () => {
        try {
            const data = await apiFetchJson<any>(API_ENDPOINTS.ADMIN_ALERTS, {
                query: { limit: 200 },
            });

            const list: Alert[] = Array.isArray(data?.alerts) ? data.alerts : [];
            const summary = data?.summary || {};

            set({
                alerts: list,
                alertsSummary: {
                    critical: Number(summary.critical ?? 0),
                    warning: Number(summary.warning ?? 0),
                    info: Number(summary.info ?? 0),
                },
            });
        } catch (err) {
            console.error("Error fetching alerts:", err);
            set({
                alerts: [],
                alertsSummary: { critical: 0, warning: 0, info: 0 },
            });
        }
    },

    fetchServiceHealth: async () => {
        const checks: Array<{ name: string; endpoint: string }> = [
            { name: "Backend API", endpoint: API_ENDPOINTS.HEALTH },
            { name: "Database", endpoint: API_ENDPOINTS.DATABASE_HEALTH },
            { name: "Discord Bot", endpoint: API_ENDPOINTS.BOT_HEALTH },
        ];

        const results = await Promise.all(
            checks.map(async (c): Promise<ServiceHealth> => {
                const started = Date.now();
                try {
                    const res = await apiFetchJson<any>(c.endpoint);
                    const elapsed = Date.now() - started;

                    let ok = true;
                    if (c.endpoint === API_ENDPOINTS.DATABASE_HEALTH) {
                        ok =
                            Boolean(res?.postgresql?.ok) && Boolean(res?.redis?.ok);
                    } else if (c.endpoint === API_ENDPOINTS.BOT_HEALTH) {
                        ok = Boolean(res?.healthy);
                    } else if (c.endpoint === API_ENDPOINTS.HEALTH) {
                        ok = String(res?.status || "").toLowerCase() === "ok";
                    }

                    return {
                        service: c.name,
                        status: ok ? "healthy" : "degraded",
                        response_time_ms: elapsed,
                        last_check: _nowIso(),
                        details: ok
                            ? undefined
                            : JSON.stringify(res).slice(0, 300),
                    };
                } catch (err) {
                    const elapsed = Date.now() - started;
                    return {
                        service: c.name,
                        status: "down",
                        response_time_ms: elapsed,
                        last_check: _nowIso(),
                        details:
                            err instanceof Error ? err.message : "Connection failed",
                    };
                }
            }),
        );

        set({ serviceHealth: results });
    },

    resolveAlert: async (alertId: string) => {
        try {
            await apiFetchJson<any>(API_ENDPOINTS.ADMIN_ALERT_RESOLVE(alertId), {
                method: "POST",
            });
            await get().fetchAlerts();
            return { success: true };
        } catch (err) {
            return {
                success: false,
                error: err instanceof Error ? err.message : "Unknown error",
            };
        }
    },

    refresh: async () => {
        set({ isLoading: true, error: null });
        try {
            await Promise.all([
                get().fetchSystemMetrics(),
                get().fetchAlerts(),
                get().fetchServiceHealth(),
            ]);
        } catch (err) {
            set({
                error: err instanceof Error ? err.message : "Unknown error",
            });
        } finally {
            set({ isLoading: false });
        }
    },

    startPolling: () => {
        _pollRefCount += 1;
        if (_pollRefCount !== 1) return;

        void get().refresh();
        if (typeof window === "undefined") return;

        _pollHandle = window.setInterval(() => {
            void get().fetchSystemMetrics();
            void get().fetchAlerts();
        }, 30000);
    },

    stopPolling: () => {
        _pollRefCount = Math.max(0, _pollRefCount - 1);
        if (_pollRefCount !== 0) return;
        if (_pollHandle === null) return;

        window.clearInterval(_pollHandle);
        _pollHandle = null;
    },
}));
